import { GenericAbortSignal } from 'axios';

import { Location, LocationOptions } from 'models/flexible/Location';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminLocationIndexFields,
  AdminLocationIndexInclude,
  AdminLocationMutationParams,
  AdminLocationShowFields,
  AdminLocationShowInclude,
} from 'services/api/fields/admin/location';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminLocationsQueryKeys = {
  index: ['adminLocations'] as const,
  show: (id: string) => ['adminLocations', id] as const,
};

const URL = 'admin/locations';

export type AdminLocationIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  state?: string;
};

export async function adminIndexLocations<I extends AdminLocationIndexInclude = never>(
  params: AdminLocationIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<Location<AdminLocationIndexFields<I>>>> {
  const include = includes?.length ? includes.join(',') : undefined;
  return http.get<Paginated<LocationOptions>>(URL, { params: { ...params, include }, signal }).then((res) => ({
    ...res.data,
    data: res.data.data.map((x) => new Location(x)),
  })) as unknown as Promise<Paginated<Location<AdminLocationIndexFields<I>>>>;
}

export async function adminShowLocation<I extends AdminLocationShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Location<AdminLocationShowFields<I>>> {
  return http
    .get<LocationOptions>(`${URL}/${id}`, { params: { include: includes?.join(',') }, signal })
    .then((res) => new Location(res.data)) as Promise<Location<AdminLocationShowFields<I>>>;
}

export async function createLocation(params: AdminLocationMutationParams) {
  return http.post<LocationOptions>(URL, params).then((res) => new Location(res.data));
}

export async function updateLocation(id: string, params: AdminLocationMutationParams) {
  return http.patch<LocationOptions>(`${URL}/${id}`, params).then((res) => new Location(res.data));
}

export async function archiveLocation(id: string) {
  return http.delete<{ data: LocationOptions }>(`${URL}/${id}`);
}
